'use client';

import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

interface SlideOverPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

export default function SlideOverPanel({ isOpen, onClose, title, children }: SlideOverPanelProps) {
  const { darkMode } = useDarkMode();

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-40 lg:hidden">
      <div
        className={`fixed inset-0 touch-manipulation backdrop-blur-sm transition-opacity ${
          darkMode ? 'bg-gray-900/50' : 'bg-white/50'
        }`}
        onClick={onClose}
      />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-closed:translate-x-full sm:duration-700"
            >
              <div
                className={`flex h-full flex-col overflow-y-scroll shadow-xl ${
                  darkMode ? 'bg-gray-900' : 'bg-white'
                }`}
              >
                <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
                  <div className="flex items-start justify-between">
                    <DialogTitle
                      className={`text-base font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                    >
                      {title}
                    </DialogTitle>
                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        onClick={onClose}
                        className={`relative touch-manipulation rounded-md focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden ${
                          darkMode
                            ? 'bg-gray-900 text-gray-400 hover:text-gray-300'
                            : 'bg-white text-gray-400 hover:text-gray-500'
                        }`}
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon aria-hidden="true" className="size-6" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="relative mx-auto mt-6 w-full max-w-7xl flex-1 px-4 sm:px-6 lg:px-8">
                  {children}
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
